"use client";

import { useEffect, useState } from "react";
import { useAuth, useSession } from "@clerk/nextjs";
import { Icon } from "@iconify/react";
import { endSession } from "@/lib/sessionEnd";

const SHOW_COUNTDOWN_UNDER_MS = 24 * 60 * 60 * 1000;

function toMs(value: unknown) {
  if (!value) return null;
  const ms = new Date(value as string | number | Date).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * El navegador no siempre recibe el expireAt real de la sesión de soporte, así
 * que se usa también createdAt + sessionSeconds (va en el claim `act`) y gana
 * el más cercano.
 */
function supportExpireAt(session: ReturnType<typeof useSession>["session"], actor: Record<string, unknown> | null | undefined) {
  const candidates = [toMs(session?.expireAt)];
  const extra = (actor?.additionalProperties as Record<string, unknown> | undefined) || {};
  const seconds = Number(actor?.sessionSeconds ?? extra.sessionSeconds);
  const created = toMs(session?.createdAt);
  if (created && seconds > 0) candidates.push(created + seconds * 1000);
  const valid = candidates.filter((v): v is number => v !== null);
  return valid.length ? Math.min(...valid) : null;
}

function useSecondsLeft(expireAt: number | null) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expireAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expireAt]);
  return expireAt ? Math.max(0, Math.round((expireAt - now) / 1000)) : null;
}

/** Aviso propio para sesiones de soporte (actor token); reemplaza el botón flotante de Clerk. */
export default function SupportSessionBadge() {
  const { actor } = useAuth();
  const { session } = useSession();
  const expireAt = actor ? supportExpireAt(session, actor as Record<string, unknown>) : null;
  const left = useSecondsLeft(expireAt);
  const countdown =
    left !== null && left * 1000 < SHOW_COUNTDOWN_UNDER_MS
      ? `${String(Math.floor(left / 60)).padStart(2, "0")}:${String(left % 60).padStart(2, "0")}`
      : null;

  if (!actor || !session) return null;
  const user = session.user;
  const name = user?.fullName || user?.username || user?.primaryEmailAddress?.emailAddress || "este usuario";

  return (
    <div className="fixed bottom-4 left-4 z-[9999] flex max-w-sm items-center gap-3 rounded-xl border border-warning/40 bg-content1 px-3 py-2 text-foreground shadow-lg">
      <Icon icon="solar:shield-user-bold-duotone" className="shrink-0 text-xl text-warning" />
      <div className="min-w-0 text-xs">
        <p className="truncate font-medium">Acceso de soporte como {name}</p>
        {countdown ? (
          <p className="text-default-500">
            Se cierra en <span className="font-mono">{countdown}</span>
          </p>
        ) : (
          <p className="text-default-500">Se cierra sola al cumplirse el tiempo del enlace.</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => endSession("signed_out")}
        className="shrink-0 rounded-lg bg-default-100 px-2 py-1 text-xs font-medium hover:bg-default-200"
      >
        Salir
      </button>
    </div>
  );
}
